import React, { useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import { useLastLiftMessage, useDirectionForLift } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "../iconDefinition/IconDefinition";
// INTERFACES
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import { defaultLiftPanelTab } from "../../../types/monitoringAccess";
import { useCanOpenControlPanel } from "../../../customHooks/useScopedPermission";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import { usageToDoorIconFiltered } from "../../../utils/bitmaskToIcon";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { EMPTY_UUID } from "../../../utils/uuid";

function MonitoringLiftTopView({
  lift,
  bank,
  serverId,
  channelId,
  bankHasMixedDoors = false,
}: {
  lift: LiftInterface;
  bank: BankInterface;
  serverId: string;
  channelId: string;
  bankHasMixedDoors?: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const theme = useSelector((state: RootState) => state.home.theme);
  const permissionSet = useSelector((state: RootState) => state.user.permissionSet);
  const doorUnknownAsOpening = useSelector(
    (state: RootState) => state.monitoring.doorUnknownAsOpening,
  );
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode,
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => lift.id ?? EMPTY_UUID, [lift.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = previewFaultServiceMode || socketMessageLift.faults;
  const liftName = String(lift.name ?? "");
  // The `??` only fires when liftFloors itself is nullish — `.some()` on a present
  // array, even an empty one, already returns a real boolean. A lift with no per-floor
  // door config is un-hydrated, not a through-car: lift.fbs declares LiftFloor.front_door
  // = true, rear_door = false, and every hydrated reader (MonitoringBank.normalizeLiftFloors,
  // bankFloorOpenings.extractLiftDoorMaps, requestSerializers) already resolves an absent
  // rear door to false — match that.
  const hasFrontDoor = useMemo(
    () => lift.liftFloors?.some((lf) => lf.frontDoor) ?? true,
    [lift.liftFloors],
  );
  const hasRearDoor = useMemo(
    () => lift.liftFloors?.some((lf) => lf.rearDoor) ?? false,
    [lift.liftFloors],
  );
  const doorIcon = useMemo(
    () =>
      usageToDoorIconFiltered(
        socketMessageLift.usage,
        hasFrontDoor,
        hasRearDoor,
        doorUnknownAsOpening,
        bankHasMixedDoors,
      ),
    [socketMessageLift.usage, doorUnknownAsOpening, hasFrontDoor, hasRearDoor, bankHasMixedDoors],
  );
  const { directionIcon, lastDirectionIcon } = useDirectionForLift(liftUuid);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    // Actions is the default tab for the drawer whenever it's available.
    if (hasPermission(Permission.MonitorControlPanelActions, permissionSet ?? [])) {
      return "ACTIONS";
    }
    if (hasFaults) {
      return "FAULTS";
    }
    return defaultLiftPanelTab(permissionSet ?? []);
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  // The lift name opens the panel only when at least one of the panel's tabs
  // would render for this role (see CONTROL_PANEL_TAB_GATES); MASTER ADMIN always.
  // Deliberately NOT tied to the Banks switch: the Roles editor's Monitoring
  // Dashboard Banks block shows the lifts, it does not make their names clickable.
  const canOpenLiftControlPanel = useCanOpenControlPanel("lift");

  const onClickOpenModalElevator = (e: any) => {
    // With no tab to show, the lift name is inert; the per-tab permissions only
    // matter once the panel IS open.
    if (!canOpenLiftControlPanel) return;
    getDrawerPlacement(e);
    dispatch(
      updateModalControlPanelTab({
        liftType: "ELEVATOR",
        tab: getTabKey(),
      }),
    );
    dispatch(updateLiftSelected(lift));
    dispatch(updateBankSelected(bank));
    dispatch(updateChannelId(channelId));
    dispatch(updateServerId(serverId));
    dispatch(updateOpenModalControlPanelLift(true));
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  const displayDirection = directionIcon || lastDirectionIcon;
  const directionDimmed = !directionIcon && !!lastDirectionIcon;
  const fallbackIcon = bankHasMixedDoors ? "elDoubleOutService" : "elOutService";

  return (
    <div>
      <div
        style={{
          height: "50px",
          width: "65px",
          display: "flex",
          flexDirection: "column",
          marginLeft: "5px",
          marginRight: "5px",
        }}
        className={`monitoringView__topViewContainer__${theme}  ${
          hasFaults ? `monitoringView__dangerElevator   ` : ""
        }${hasFaults ? `monitoringView__dangerElevator__top ` : ""}${
          hasFaults ? `monitoringView__dangerElevator__bottom` : ""
        }  `}
      >
        <div
          className={`monitoringView__topViewContainer__${theme}`}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {doorIcon && (
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType="ELEVATOR"
              icon={doorIcon}
              iconSize={20}
            />
          )}
          {!doorIcon && !displayDirection && (
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType="ELEVATOR"
              icon={fallbackIcon}
              iconSize={20}
            />
          )}
          {displayDirection && (
            <span style={{ opacity: directionDimmed ? 0.3 : 1 }}>
              <IconDefinition
                isMap={false}
                expanded={false}
                liftType="ELEVATOR"
                icon={displayDirection}
                iconSize={20}
              />
            </span>
          )}
        </div>
      </div>
      {/** ELEVATOR NAME */}
      <div
        role="button"
        tabIndex={0}
        onKeyDown={emptyFunctionDeclaration}
        onClick={onClickOpenModalElevator}
        style={{
          fontSize: "9px",
          fontWeight: "bolder",
          textAlign: "center",
          marginTop: "5px",
        }}
      >
        <div>
          <Tooltip placement="top" title={liftName}>
            <span>{liftName.toUpperCase().substring(0, 6)}</span>
          </Tooltip>
        </div>
        {!hasFaults && !socketMessageLift.serviceMode && !previewFaultServiceMode && (
          <div style={{ height: "10px" }} />
        )}
        {hasFaults && (
          <div className="mt-1">
            <Tag
              style={{
                marginTop: "5px",
                fontSize: "10px",
                width: "90%",
                marginLeft: "5%",
                marginRight: "5%",
              }}
              color="red"
            >
              FAULT
            </Tag>
          </div>
        )}
        {(socketMessageLift.serviceMode || previewFaultServiceMode) &&
          (() => {
            if (previewFaultServiceMode && !socketMessageLift.serviceMode) {
              return (
                <div className="mt-1 mb-2">
                  <Tooltip placement="top" title="Energy Saving">
                    <Tag
                      style={{
                        marginTop: "5px",
                        fontSize: "10px",
                        width: "90%",
                        marginLeft: "5%",
                        marginRight: "5%",
                      }}
                      color="blue"
                    >
                      ENER
                    </Tag>
                  </Tooltip>
                </div>
              );
            }
            const display = getServiceModeDisplay(
              socketMessageLift.serviceMode?.messageType,
              socketMessageLift.serviceMode?.message?.message?.message ?? null,
            );
            if (!display) return null;
            return (
              <div className="mt-1 mb-2">
                <Tooltip placement="top" title={display.displayName}>
                  <Tag
                    style={{
                      marginTop: "5px",
                      fontSize: "10px",
                      width: "90%",
                      marginLeft: "5%",
                      marginRight: "5%",
                    }}
                    color={display.color}
                  >
                    {display.abbreviation.substring(0, 4)}
                  </Tag>
                </Tooltip>
              </div>
            );
          })()}
      </div>
    </div>
  );
}

export default React.memo(MonitoringLiftTopView);
